import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';
import { useTypedParams, useTypedSearchParams } from 'react-router-typesafe-routes';
import AppCell from '@/components/common/app-cell';
import ConversationReportForm from '@/components/conversations/conversation-report-form';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useReportReasons from '@/hooks/settings/use-report-reasons';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';

interface Props {
  type: ConversationType;
}

export default function ReportRoute({ type }: Props) {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS.REPORT || ROUTES.MESSAGES.SESSIONS.REPORT);
  const [{ reasonId }, setSearchParams] = useTypedSearchParams(
    ROUTES.MESSAGES.GROUPS.REPORT || ROUTES.MESSAGES.SESSIONS.REPORT,
  );
  const { reasons, isLoading } = useReportReasons(type);

  return (
    <PageLayout
      title={type === 'session' ? t('conversation.report.session') : t('conversation.report.group')}
      headerConfig={{
        navigateBack: true,
        headerLabel: t(type === 'session' ? 'conversation.report.reason-title' : 'conversation.report.reason-title'),
      }}>
      <LoadingLayout promise={isLoading}>
        {() =>
          reasonId !== undefined ? (
            <ConversationReportForm
              sessionId={id}
              reasonId={reasonId}
              type={type}
              onDone={() => navigate(-2)}
            />
          ) : (
            <div className='flex w-full flex-col gap-2 pb-safe-or-16 desktop:pb-32'>
              <LoadingLayout promise={loaderData?.promises}>
                {() => (
                  <div className='flex flex-col items-stretch'>
                    {reasons.map(({ ID, Reason }) => (
                      <AppCell
                        key={ID}
                        label={Reason}
                        labelWrapperClassName='whitespace-break-spaces'
                        chevron
                        onClick={() => setSearchParams({ reasonId: ID })}
                      />
                    ))}
                  </div>
                )}
              </LoadingLayout>
            </div>
          )
        }
      </LoadingLayout>
    </PageLayout>
  );
}
